import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import Layout from '../components/Layout';
import useAggiornamentoPeriodico from '../hooks/useAggiornamentoPeriodico';
import useVersioniViste from '../hooks/useVersioniViste';
import ModificataDaAltri from '../components/ModificataDaAltri';
import { messaggioAzione, avvisaConflitto } from '../utils/eliminazione';
import ConflittoVersione, { eConflitto } from '../components/ConflittoVersione';
import stiliComuni from '../components/competenze/stili';
import { usePuoScrivere } from '../utils/permessi';
import PasswordTemporanea from '../components/PasswordTemporanea';
import NuovoAccountCliente from '../components/NuovoAccountCliente';
import AvvisoDatiComuni from '../components/competenze/AvvisoDatiComuni';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { puoAccedere } from '../utils/permessi';

export default function Clienti() {
  const [clienti, setClienti] = useState([]);
  const [cerca, setCerca] = useState('');
  const [mostraForm, setMostraForm] = useState(false);
  const [modifica, setModifica] = useState(null);
  const [credenzialiNuove, setCredenzialiNuove] = useState(null);
  const puoScrivere = usePuoScrivere('intermittenti');
  const { utente } = useAuth();
  const vedeAnagrafiche = puoAccedere(utente, 'anagrafiche');
  const puoAccount = usePuoScrivere('account_clienti');   // creare un account cliente (reset, password e disattivazione stanno in Impostazioni → Utenti)
  const [nuovoAccount, setNuovoAccount] = useState(false);
  const [daDisattivare, setDaDisattivare] = useState(null);   // azienda per cui si chiede la conferma
  const [erroreElenco, setErroreElenco] = useState('');
  const disattivando = useRef(false);
  const versioni = useVersioniViste();   // riga "modificata da altri" quando l'aggiornamento automatico porta una versione diversa da quella vista

  const caricaClienti = useCallback(async ({ silenzioso = false } = {}) => {
    try {
      const res = await api.get('/aziende');
      versioni.aggiorna(res.data, { silenzioso });
      setClienti(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [versioni]);

  useEffect(() => { caricaClienti(); }, [caricaClienti]);
  useAggiornamentoPeriodico(() => caricaClienti({ silenzioso: true }), { attivo: !mostraForm && !credenzialiNuove && !nuovoAccount && !daDisattivare });

  const filtrati = clienti.filter(c => {
    if (!cerca) return true;
    const q = cerca.toLowerCase();
    return (c.ragione_sociale || '').toLowerCase().includes(q) || (c.codice_fiscale || '').toLowerCase().includes(q);
  });

  // Disattiva azienda: l'azienda va nel Cestino e i suoi account vengono disattivati. Versione vista obbligatoria, protezione dal doppio clic.
  const disattivaAzienda = async (c) => {
    if (disattivando.current) return;
    disattivando.current = true;
    setErroreElenco('');
    try {
      await api.put(`/aziende/${c.id}/disattiva`, { version: versioni.versioneVista(c) });
      setDaDisattivare(null);
      caricaClienti();
    } catch (err) {
      setDaDisattivare(null);
      setErroreElenco(avvisaConflitto(err, messaggioAzione(err, 'Disattivazione non riuscita')));
      if (eConflitto(err)) caricaClienti();   // qualcun altro l'ha modificata: si mostra la riga aggiornata
    } finally {
      disattivando.current = false;
    }
  };

  return (
    <Layout>
      <div style={styles.topBar}>
        <h2 style={{ margin: 0 }}>Aziende ({filtrati.length})</h2>
        {puoAccount && <button onClick={() => setNuovoAccount(true)} style={styles.btn}>+ Nuovo account cliente</button>}
      </div>

      {erroreElenco && <div style={styles.errore}>{erroreElenco}</div>}

      <input
        type="text"
        placeholder="Cerca per ragione sociale o codice fiscale..."
        value={cerca}
        onChange={e => setCerca(e.target.value)}
        style={styles.inputCerca}
      />

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Ragione Sociale</th>
              <th style={styles.th}>Codice Fiscale / P.IVA</th>
              <th style={styles.th}>Indirizzo</th>
              <th style={styles.th}>Login (Email)</th>
              <th style={styles.th}>Accesso</th>
              {puoScrivere && <th style={styles.th}>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {filtrati.map(c => (
              <tr key={c.id} style={styles.tr}>
                <td style={styles.td}>
                  {c.ragione_sociale}
                  {versioni.cambiata(c) && <ModificataDaAltri onRiconosci={() => versioni.riconosci(c)} />}
                  {c.collegata && vedeAnagrafiche && <div><Link to={`/anagrafiche/${c.anagrafica_id}`} style={{ fontSize: '0.78rem' }}>Scheda in Anagrafiche</Link></div>}
                </td>
                <td style={styles.td}>{c.codice_fiscale}</td>
                <td style={styles.td}>{c.indirizzo_completo || '—'}</td>
                <td style={styles.td}>{c.utente_email || '—'}</td>
                <td style={styles.td}>{statoAccesso(c)}</td>
                {puoScrivere && <td style={styles.td}>
                  {puoScrivere && <button onClick={() => { setModifica(c); setMostraForm(true); }} style={styles.btnSmall}>{c.collegata ? 'Dati' : 'Modifica'}</button>}
                  {puoScrivere && <button onClick={() => setDaDisattivare(c)} style={{ ...styles.btnSmallDanger, marginLeft: '0.8rem' }}>Disattiva azienda</button>}
                </td>}
              </tr>
            ))}
            {filtrati.length === 0 && (
              <tr><td colSpan={puoScrivere ? 6 : 5} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>Nessuna azienda trovata</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {nuovoAccount && (
        <NuovoAccountCliente
          onChiudi={() => setNuovoAccount(false)}
          onSalvato={(nuovo) => {
            setNuovoAccount(false);
            if (nuovo?.password_temporanea) setCredenzialiNuove({ email: nuovo.email, password: nuovo.password_temporanea });
            caricaClienti();
          }}
        />
      )}
      {daDisattivare && (
        <div style={stiliComuni.overlay} role="dialog" aria-label="Disattiva azienda">
          <div style={{ ...stiliComuni.modal, maxWidth: '480px' }}>
            <h3 style={{ marginTop: 0 }}>Disattiva azienda</h3>
            <p style={{ lineHeight: 1.5 }}>
              Disattivare l'azienda {daDisattivare.ragione_sociale}? L'azienda va nel Cestino e i suoi account ({daDisattivare.account_attivi ?? 0}) vengono disattivati:
              gli utenti non potranno più accedere. Le comunicazioni già inviate restano.
            </p>
            <div style={stiliComuni.azioni}>
              <button type="button" onClick={() => setDaDisattivare(null)} style={stiliComuni.btnSecondary}>Annulla</button>
              <button type="button" onClick={() => disattivaAzienda(daDisattivare)} style={{ ...stiliComuni.btn, background: '#c00' }}>Disattiva azienda</button>
            </div>
          </div>
        </div>
      )}
      {mostraForm && (
        <FormCliente
          cliente={modifica}
          onChiudi={() => { setMostraForm(false); setModifica(null); }}
          onSalvato={(nuovo) => {
            setMostraForm(false);
            setModifica(null);
            if (nuovo?.password_temporanea) setCredenzialiNuove({ email: nuovo.email, password: nuovo.password_temporanea });
            caricaClienti();
          }}
        />
      )}
      {credenzialiNuove && (
        <PasswordTemporanea
          email={credenzialiNuove.email}
          password={credenzialiNuove.password}
          onChiudi={() => setCredenzialiNuove(null)}
        />
      )}
    </Layout>
  );
}

function statoAccesso(c) {
  if (!c.utente_id) return <span style={{ color: '#999' }}>—</span>;
  if (c.utente_deve_cambiare_password) return <span style={{ color: '#b45309', fontSize: '0.85rem' }}>Password temporanea</span>;
  return <span style={{ color: '#15803d', fontSize: '0.85rem' }}>Attivo</span>;
}

function FormCliente({ cliente, onChiudi, onSalvato }) {
  const [form, setForm] = useState(
    cliente
      ? { ragione_sociale: cliente.ragione_sociale, codice_fiscale: cliente.codice_fiscale, indirizzo: cliente.indirizzo || '', version: cliente.version }
      : { ragione_sociale: '', codice_fiscale: '', indirizzo: '' }
  );
  const [errore, setErrore] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const [conflitto, setConflitto] = useState(null);

  const salvaModifica = async (dati) => {
    setErrore('');
    setSalvataggio(true);
    try {
      await api.put(`/aziende/${cliente.id}`, dati);
      setConflitto(null);
      onSalvato();
    } catch (err) {
      if (eConflitto(err)) setConflitto(err.response.data);
      else setErrore(err.response?.data?.errore || 'Errore nel salvataggio');
    } finally {
      setSalvataggio(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cliente) {
      return salvaModifica({
        ragione_sociale: form.ragione_sociale,
        codice_fiscale: form.codice_fiscale,
        indirizzo: form.indirizzo,
        version: form.version,
      });
    }
  };

  const aggiorna = (campo, valore) => setForm(prev => ({ ...prev, [campo]: valore }));

  // Scheda collegata all'anagrafica: i dati comuni sono in sola lettura (si modificano in Anagrafiche); nessun altro campo è modificabile da qui
  if (cliente?.collegata) {
    return (
      <div style={styles.overlay} role="dialog" aria-label="Dati dell'azienda">
        <div style={styles.modal}>
          <h3>Azienda</h3>
          <AvvisoDatiComuni anagraficaId={cliente.anagrafica_id} onNavigare={onChiudi} elenco="nome, codice fiscale / partita IVA e indirizzo" />
          <div style={styles.campo}>
            <label htmlFor="az-ro-nome">Ragione Sociale</label>
            <input id="az-ro-nome" type="text" value={cliente.ragione_sociale || ''} readOnly disabled style={styles.input} />
          </div>
          <div style={styles.campo}>
            <label htmlFor="az-ro-cf">Codice Fiscale / P.IVA</label>
            <input id="az-ro-cf" type="text" value={cliente.codice_fiscale || ''} readOnly disabled style={styles.input} />
          </div>
          <div style={styles.campo}>
            <label htmlFor="az-ro-ind">Indirizzo</label>
            <input id="az-ro-ind" type="text" value={cliente.indirizzo_completo || ''} readOnly disabled style={styles.input} />
          </div>
          <div style={styles.modalActions}>
            <button type="button" onClick={onChiudi} style={styles.btn}>Chiudi</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h3>Modifica azienda</h3>
        <form onSubmit={handleSubmit}>
          {errore && <div style={styles.errore}>{errore}</div>}
          <div style={styles.campo}>
            <label>Ragione Sociale</label>
            <input type="text" value={form.ragione_sociale} onChange={e => aggiorna('ragione_sociale', e.target.value)} required style={styles.input} />
          </div>
          <div style={styles.campo}>
            <label>Codice Fiscale / P.IVA</label>
            <input type="text" value={form.codice_fiscale} onChange={e => aggiorna('codice_fiscale', e.target.value)} required maxLength={16} style={styles.input} />
          </div>
          <div style={styles.campo}>
            <label>Indirizzo</label>
            <input type="text" value={form.indirizzo} onChange={e => aggiorna('indirizzo', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.modalActions}>
            <button type="button" onClick={onChiudi} style={styles.btnSecondary}>Annulla</button>
            <button type="submit" disabled={salvataggio} style={styles.btn}>
              {salvataggio ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
      {conflitto && (
        <ConflittoVersione
          conflitto={conflitto}
          inCorso={salvataggio}
          onRicarica={() => {
            const a = conflitto.attuale;
            setForm(f => ({ ...f, ragione_sociale: a.ragione_sociale, codice_fiscale: a.codice_fiscale, indirizzo: a.indirizzo || '', version: a.version }));
            setConflitto(null);
          }}
          onSovrascrivi={() => salvaModifica({
            ragione_sociale: form.ragione_sociale, codice_fiscale: form.codice_fiscale, indirizzo: form.indirizzo, version: conflitto.attuale.version,
          })}
        />
      )}
    </div>
  );
}


const styles = {
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' },
  inputCerca: { width: '100%', padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', marginBottom: '1rem', boxSizing: 'border-box' },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  th: { textAlign: 'left', padding: '0.8rem', background: '#f8f9fa', borderBottom: '2px solid #eee', fontSize: '0.85rem', color: '#555' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '0.7rem 0.8rem', fontSize: '0.9rem' },
  btn: { background: '#232323', color: 'white', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' },
  btnSecondary: { background: 'white', color: '#333', border: '1px solid #ddd', padding: '0.6rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' },
  btnSmall: { background: '#eef', color: '#232323', border: 'none', padding: '0.3rem 0.7rem', borderRadius: '4px', cursor: 'pointer', marginRight: '0.3rem', fontSize: '0.8rem' },
  btnSmallDanger: { background: '#fee', color: '#c00', border: 'none', padding: '0.3rem 0.7rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { background: 'white', padding: '2rem', borderRadius: '12px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto' },
  campo: { marginBottom: '1rem', flex: 1 },
  input: { width: '100%', padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box', marginTop: '0.3rem' },
  row: { display: 'flex', gap: '1rem' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' },
  errore: { background: '#fee', color: '#c00', padding: '0.7rem', borderRadius: '6px', marginBottom: '1rem' },
};
